import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useMemo } from "react";

import {
  getAvailableChildChecks,
  getAvailableSelectOptions,
} from "./order-utils";

type EmploymentVerificationPath =
  "/api/internal/client-portal/check-arguments-templates/employment-verification/options/";

export function useMinCheckPrice(
  children: components["schemas"]["CheckListChild"][],
  selectOptions: components["schemas"]["UmbrellaCheckSelectOption"][],
  price: number | null | undefined,
  customerGroupId: string,
  selectFieldEndpoint?: string
) {
  const { data: selectFieldOptions } = useClientPortalAPI(
    selectFieldEndpoint
      ? (selectFieldEndpoint as EmploymentVerificationPath)
      : null,
    { customer_group_id: customerGroupId }
  );

  const minCheckPrice = useMemo(() => {
    const availableSelectOptionPrices = getAvailableSelectOptions(
      selectOptions
    )?.flatMap((option) => (option.price ? [option.price] : []));

    if (availableSelectOptionPrices?.length) {
      return Math.min(...availableSelectOptionPrices);
    }

    if (selectFieldOptions && selectFieldOptions?.length) {
      const prices = selectFieldOptions.map(
        (option) => Number(option.price) || 0
      );
      return Math.min(...prices);
    }

    const availableChildCheckPrices = getAvailableChildChecks(
      children
    )?.flatMap((child) => (child.price ? [child.price] : []));

    if (availableChildCheckPrices?.length) {
      return Math.min(...availableChildCheckPrices);
    }

    return price;
  }, [children, selectOptions, price, selectFieldOptions]); // Recompute when `check` or `data` changes

  return minCheckPrice;
}
